<template>
  <div class="app-container">
    <header-bar></header-bar>
    <div class="content-wrapper">
      <div class="empty-left"></div>
      <div class="left-sidenotes"></div>
      <div class="main">
        <div v-if="loading">Loading meeting details...</div>
        <div v-else-if="error">{{ error }}</div>
        <div v-else>
          <div class="viz-area">
            <p class="header">{{meeting?.town}} {{ meeting?.type }}</p>
            <span class="divider"></span>
            <p class="cardsubhead">{{ formatDate(meeting?.date) }}</p>
            <p class="cardsubhead">Topics: 
              <span v-if="meeting?.tags && meeting?.tags.length > 0">
                {{ meeting?.tags.join(', ') }}
              </span>
              <span v-else>
                Currently Not Available
              </span></p>
            <p class="cardsubhead">
              Transcript: <span 
                  :class="{ 'available-link': meeting?.files?.plainText }"
                  @click="meeting?.files?.plainText && goToTranscript(meeting.id)"
                >{{ meeting?.files?.plainText ? 'Available' : 'Unavailable' }}</span> &nbsp;•&nbsp;  
              Minutes: <span 
                :class="{ 'available-link': meeting?.files?.minutes }"
                @click="meeting?.files?.minutes && openDocument('minutes')"
              >{{ meeting?.files?.minutes ? 'Available' : 'Unavailable' }}</span> &nbsp;•&nbsp;  
              Agenda: <span 
                :class="{ 'available-link': meeting?.files?.other }"
                @click="meeting?.files?.other && openDocument('other')"
              >{{ meeting?.files?.other ? 'Available' : 'Unavailable' }}</span>
            </p>              
            <p class="cardsubhead">
              AI Summary Confidence Level: 
              <span class="tooltip" :data-tooltip="confidenceTooltip">
                {{ meeting.confidence }}
              </span>
            </p>            <span class="divider"></span>
            <div class="aiSum">
              <div v-html="renderMarkdown(meeting.aiSummary)"></div>
            </div>
          </div>
        </div>
      </div>
      <div class="right-sidenotes"></div>
    </div>
  </div>
</template>

<script>
import { doc, getDoc } from 'firebase/firestore';
import { db } from '@/firebase';
import HeaderBar from './RevisedHeader.vue';
import { marked } from 'marked';
import DOMPurify from 'dompurify';

export default {
  name: 'MeetingView',
  components: {
    HeaderBar,
  },
  data() {
    return {
      meeting: null,
      loading: true,
      error: null,
    }
  },
  computed: {
    confidenceTooltip() {
      if (this.meeting && this.meeting.confidence) {
        const status = this.meeting.confidence.toLowerCase();
        if (status === 'not reviewed') {
          return "Our editor has not reviewed this content for accuracy.";
        } else if (status === 'reviews' || status === 'reviewed') {
          return "Our editor has reviewed this content, if you find an error please let us know.";
        }
      }
      return "Status not Recognized";
    }
  },
  methods: {
    formatDate(date) {
      if (!date) return '';
      const months = ['Jan.', 'Feb.', 'Mar.', 'Apr.', 'May', 'June', 'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.'];
      const dateObj = date instanceof Date ? date : new Date(`${date}T12:00:00Z`);
      const month = months[dateObj.getMonth()];
      const day = dateObj.getDate();
      const year = dateObj.getFullYear();
      return `${month} ${day}, ${year}`;
    },

    openDocument(type) {
      const fileUrl = this.meeting?.files?.[type];
      if (fileUrl) {
        window.open(fileUrl, '_blank');
      }
    },

    goToTranscript(meetingId) {
      this.$router.push(`/transcript/${meetingId}`);
    },

    async fetchMeetingDetails() {
      this.loading = true;
      this.error = null;
      
      try {
        const meetingId = this.$route.params.id;
        const meetingRef = doc(db, 'meetingInfo', meetingId);
        const docSnap = await getDoc(meetingRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const date = new Date(`${data.date}T12:00:00Z`);
          
          this.meeting = {
            id: docSnap.id,
            ...data,
            date
          };
        } else {
          this.error = 'Meeting not found';
        }
      } catch (error) {
        console.error('Error fetching meeting details:', error);
        this.error = 'Failed to load meeting details. Please try again later.';
      } finally {
        this.loading = false;
      }
    },
    renderMarkdown(content) {
      const dirty = marked(content || '');
      const clean = DOMPurify.sanitize(dirty);
      return clean;
    },
  },

  created() {
    this.fetchMeetingDetails();
  },

  watch: {
    '$route.params.id': {
      handler(newId) {
        if (newId) {
          this.fetchMeetingDetails();
        }
      },
      immediate: true
    }
  }
}
</script>

<style scoped>
* {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
  }

.app-container {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100vh;
  overflow: hidden;
}

.content-wrapper {
  position: relative;
  flex: 1;
  display: flex;
  overflow: hidden;
  padding-top: 64px;
}

.empty-left{
  display: flex;
  flex: 1;
  background-color: #fffff8;
}

.right-sidenotes, .left-sidenotes {
  display: flex;
  flex: 1;
  background-color: #fffff8;
}

.main {
  flex: 3;
  background-color: #fffff8;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: flex-start;
  overflow: auto;
  max-width: 1200px;
}

.main .header {
  text-align: left;
  padding-top: 20px;
  padding-bottom: 30px;
  font-size: 2rem;
  font-weight: 600;
  font-family: inherit;
  color: #545051;
}

.divider {
  display: block;
  width: 100%;
  height: 1px;
  background-color: #545051;
  margin: 10px 0;
}

.cardsubhead {
  text-align: left;
  font-size: 1rem;
  font-family: inherit;
  color: #545051;
  line-height: 1.6rem;
}

.available-link {
  color: #545051;
  cursor: pointer;
  text-decoration: underline dashed;
  text-underline-offset: 2px;
  text-decoration-skip-ink: none;
}

.available-link:hover {
  color: #8E9C71;
}

.viz-area {
  flex: 1;
  width: auto;
  box-sizing: border-box;
  overflow: auto;
  position: relative;
}

/* Markdown Styles */
.aiSum ::v-deep p {
    margin-bottom: 1rem;
  }
  .aiSum ::v-deep p:has(strong),
  .aiSum ::v-deep p:has(b) {
    margin-bottom: 0.75rem;
    margin-top: 2rem;
  }

/* Responsive Design */
@media (max-width: 768px) {
  .empty-left,
  .right-sidenotes,
  .left-sidenotes {
    display: none;
  }

  .main {
    flex: 1;
    max-width: 100%;
    padding: 0 1rem; /* Optional: add padding for better readability */
  }

  .viz-area {
    overflow: auto;
  }
}

@media (min-width: 768px) {
  .empty-left, .left-sidenotes {
    display: none;
  }

  .right-sidenotes {
    display: flex;
  }

  .main {
    flex: 2;
    max-width: 100%;
    padding: 0 2rem;
  }
}

.tooltip {
  position: relative;
  cursor: pointer;
}

.tooltip:hover::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: -2.75rem;
  left: 100%;
  transform: translateX(-10%);
  background-color: #545051; /* Inverse of your light background */
  color: #fffff8; /* Inverse of the text color */
  padding: 0.5rem;
  border-radius: 0.3rem;
  white-space: nowrap;
  z-index: 10;
  font-size: 0.8rem;
  opacity: 0;
  transition: opacity 0.3s;
  pointer-events: none;
}

.tooltip:hover::after {
  opacity: 1;
}

@media (min-width: 1024px) {
  .empty-left, .right-sidenotes, .left-sidenotes {
    display: flex;
  }

  .main {
    flex: 3;
    max-width: 1200px;
    padding: 0;
  }
}


</style>